<!-- Interactive HMI input controls (slider / toggle / number+text input).
     Unlike SynWidget (read-only value display), a control WRITES: on user
     interaction in Run mode it emits `change(value)`, which the viewer turns
     into a set_variable (memory) or a control_write (process, gated by command
     mode). Its CURRENT value comes from the node's dataBinding, resolved by
     design_render exactly like any other shape — so a slider/toggle reflects
     live state and round-trips. In edit mode (`live=false`) it's inert (no
     pointer capture) so it can be selected and dragged on the canvas. -->
<template>
  <div class="syn-ctrl" :class="[kind, { live }]">
    <!-- Range slider -->
    <template v-if="kind === 'slider'">
      <div class="ctrl-head" v-if="p.label || p.showValue !== false">
        <span v-if="p.label" class="ctrl-label">{{ p.label }}</span>
        <span v-if="p.showValue !== false" class="ctrl-val">{{ slider }}<span v-if="p.unit" class="ctrl-unit">{{ p.unit }}</span></span>
      </div>
      <input
        class="ctrl-range" type="range"
        :min="min" :max="max" :step="step" :value="slider"
        :disabled="!live"
        :style="{ accentColor: accent }"
        @input="onSlide(($event.target as HTMLInputElement).value)"
        @pointerdown.stop="dragging = true" @click.stop
        @pointerup="endDrag" @change="endDrag"
      />
    </template>

    <!-- On/off toggle switch -->
    <template v-else-if="kind === 'toggle'">
      <button
        type="button" class="ctrl-switch" :class="{ on: isOn }"
        :disabled="!live"
        :style="{ '--ctrl-accent': accent }"
        @pointerdown.stop @click.stop="live && onToggle()"
      >
        <span class="ctrl-knob"></span>
      </button>
      <span v-if="p.label || onOffLabel" class="ctrl-label">{{ p.label || onOffLabel }}</span>
    </template>

    <!-- Variable selector / filter: drives a $var consumed by metric bindings.
         Options are a fixed list (custom) or label values resolved server-side. -->
    <template v-else-if="kind === 'select'">
      <span v-if="p.label" class="ctrl-label">{{ p.label }}</span>
      <select
        v-if="!p.multi"
        class="ctrl-input"
        :disabled="!live"
        :value="selSingle"
        @pointerdown.stop @click.stop
        @change="onSelectSingle(($event.target as HTMLSelectElement).value)"
      >
        <option v-if="p.includeAll" :value="ALL">All</option>
        <option v-for="o in optionList" :key="o" :value="o">{{ o }}</option>
        <option v-if="!optionList.length && !p.includeAll" disabled value="">{{ live ? 'Sin valores' : '(valores en Run)' }}</option>
      </select>
      <div v-else class="ctrl-multi" @pointerdown.stop @click.stop>
        <label v-if="p.includeAll" class="ctrl-opt"><input type="checkbox" :checked="!selMulti.length" :disabled="!live" @change="selectAll()" /> All</label>
        <label v-for="o in optionList" :key="o" class="ctrl-opt"><input type="checkbox" :checked="selMulti.includes(o)" :disabled="!live" @change="toggleOne(o)" /> {{ o }}</label>
      </div>
    </template>

    <!-- Number / text input -->
    <template v-else>
      <span v-if="p.label" class="ctrl-label">{{ p.label }}</span>
      <div class="ctrl-inwrap">
        <input
          class="ctrl-input"
          :type="p.inputKind === 'text' ? 'text' : 'number'"
          :placeholder="p.placeholder || ''"
          :min="p.min" :max="p.max" :step="step"
          :value="live ? draft : (p.inputKind === 'text' ? '' : num)"
          :disabled="!live"
          @pointerdown.stop @click.stop
          @input="draft = ($event.target as HTMLInputElement).value"
          @change="commitInput"
          @keydown.enter="commitInput"
        />
        <span v-if="p.unit" class="ctrl-unit">{{ p.unit }}</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    kind: string;
    p: Record<string, any>;
    value?: number | null;
    /** Resolved option list for a 'select' control (label_values source). */
    options?: string[];
    color?: string;
    live?: boolean;
  }>(),
  { live: false },
);
const emit = defineEmits<{ (e: 'change', value: number | string | string[]): void }>();

const min = computed(() => Number(props.p.min ?? 0));
const max = computed(() => { const m = Number(props.p.max ?? 100); return m === min.value ? min.value + 1 : m; });
const step = computed(() => { const s = Number(props.p.step); return Number.isFinite(s) && s > 0 ? s : 1; });
const accent = computed(() => props.color || (props.p.color as string) || 'var(--color-brand-600, #2c70b9)');

// Current numeric value from the binding (design-time falls back to mid-range).
const num = computed<number>(() => {
  if (props.value != null && Number.isFinite(Number(props.value))) return Number(props.value);
  return props.live ? min.value : Math.round((min.value + max.value) / 2);
});
// Optimistic local position: the thumb tracks a local draft while the user
// drags AND until the server round-trip (set_variable → render) confirms it.
// Without this, in-flight renders returning intermediate values yank the thumb
// back (drag to 80 → snaps to 60). The draft is released when props.value
// equals it (confirmed) or shortly after the user lets go (settle fallback,
// e.g. process write-back that never echoes back the exact value).
const sliderDraft = ref<number | null>(null);
const dragging = ref(false);
const slider = computed(() => sliderDraft.value ?? num.value);
let settleTimer: ReturnType<typeof setTimeout> | null = null;
watch(() => props.value, (nv) => {
  if (dragging.value) return; // never adopt the bound value mid-drag
  if (sliderDraft.value != null && nv != null && Number(nv) === sliderDraft.value) sliderDraft.value = null;
});
function endDrag(): void {
  dragging.value = false;
  if (settleTimer) clearTimeout(settleTimer);
  // Release the draft once the value has had time to settle, so external
  // updates resume even if the confirm-match above never fires.
  settleTimer = setTimeout(() => { sliderDraft.value = null; }, 1200);
}

// Toggle on/off: on when value equals onValue (default 1) or is truthy.
const onValue = computed(() => (props.p.onValue ?? 1));
const offValue = computed(() => (props.p.offValue ?? 0));
const isOn = computed(() => {
  if (props.value == null) return false;
  return String(props.value) === String(onValue.value) || (Number(props.value) > 0 && offValue.value !== onValue.value);
});
const onOffLabel = computed(() => (isOn.value ? (props.p.onLabel ?? '') : (props.p.offLabel ?? '')));

function onSlide(v: string): void { sliderDraft.value = Number(v); emit('change', Number(v)); }
function onToggle(): void { emit('change', isOn.value ? offValue.value : onValue.value); }

// Text/number input: edit a local draft, commit on Enter/blur.
const draft = ref('');
watch(() => props.value, (v) => { if (props.live) draft.value = v == null ? '' : String(v); }, { immediate: true });
function commitInput(): void {
  if (!props.live) return;
  const raw = draft.value;
  if (props.p.inputKind === 'text') { emit('change', raw); return; }
  const n = Number(raw);
  if (Number.isFinite(n)) emit('change', n);
}

// ── Variable selector ────────────────────────────────────────────────────
const ALL = '$__all'; // sentinel: server drops it (= match any)
// Options: resolved label values (from render) take precedence over a custom list.
const optionList = computed<string[]>(() => {
  if (props.options?.length) return props.options;
  return Array.isArray(props.p.options) ? props.p.options.map(String) : [];
});
// Single selection (local + optimistic). Defaults to p.default or "All".
const selSingle = ref<string>(typeof props.p.default === 'string' ? props.p.default : (props.p.includeAll ? ALL : ''));
function onSelectSingle(v: string): void { selSingle.value = v; emit('change', v); }
// Multi selection: empty = All (no filter). Defaults from p.default (csv).
const selMulti = ref<string[]>(
  typeof props.p.default === 'string' && props.p.default ? props.p.default.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
);
function selectAll(): void { selMulti.value = []; emit('change', []); }
function toggleOne(o: string): void {
  const i = selMulti.value.indexOf(o);
  if (i >= 0) selMulti.value.splice(i, 1); else selMulti.value.push(o);
  emit('change', [...selMulti.value]);
}

onBeforeUnmount(() => { if (settleTimer) clearTimeout(settleTimer); });
</script>

<style scoped>
.syn-ctrl { width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: center; gap: 4px; font-size: 0.78rem; color: var(--color-ink, #1f2937); overflow: hidden; }
.syn-ctrl:not(.live) { pointer-events: none; } /* inert in edit mode → selectable/draggable */
.ctrl-head { display: flex; align-items: baseline; justify-content: space-between; gap: 6px; }
.ctrl-label { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctrl-val { font-weight: 700; font-variant-numeric: tabular-nums; }
.ctrl-unit { opacity: 0.7; margin-left: 1px; font-size: 0.85em; }

.ctrl-range { width: 100%; cursor: pointer; }
.syn-ctrl:not(.live) .ctrl-range { cursor: default; }

/* Toggle switch */
.syn-ctrl.toggle { flex-direction: row; align-items: center; gap: 8px; }
.ctrl-switch { position: relative; width: 42px; height: 24px; flex: 0 0 auto; border-radius: 9999px; border: none; background: var(--color-line-strong, #cbd5e1); cursor: pointer; transition: background 0.15s ease; padding: 0; }
.ctrl-switch.on { background: var(--ctrl-accent); }
.ctrl-switch:disabled { cursor: default; }
.ctrl-knob { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.3); transition: left 0.15s ease; }
.ctrl-switch.on .ctrl-knob { left: 21px; }

/* Text / number input */
.ctrl-inwrap { display: flex; align-items: center; gap: 4px; }
.ctrl-input { width: 100%; min-width: 0; box-sizing: border-box; padding: 4px 6px; border: 1px solid var(--color-line-strong, #cbd5e1); border-radius: 5px; background: var(--color-surface, #fff); color: inherit; font-size: 0.82rem; }
.ctrl-input:focus { outline: none; border-color: var(--color-brand-600, #2c70b9); }
.ctrl-input:disabled { background: var(--color-surface-2, #f1f5f9); }

/* Variable selector (multi = checkbox list) */
.ctrl-multi { display: flex; flex-direction: column; gap: 2px; overflow: auto; max-height: 100%; padding: 2px 0; }
.ctrl-opt { display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.ctrl-opt input { flex: 0 0 auto; }
</style>
